import React, { useState, useEffect } from 'react';
import {
  ChevronDown, ChevronRight, Truck, Box, Layers,
  AlertTriangle, AlertCircle, Warehouse
} from 'lucide-react';

const ListWarehouse = ({
  token,
  API_URL,
  estructuraCompleta,
  filtros,
  setFiltros,
  refrescarVista,
  almacenSeleccionado
}) => {
  // Estados de expansión
  const [expandedZones, setExpandedZones] = useState({});
  const [expandedLineas, setExpandedLineas] = useState({});
  const [expandedColumnas, setExpandedColumnas] = useState({});
  const [expandedNiveles, setExpandedNiveles] = useState({});
  // eslint-disable-next-line no-unused-vars
  const [showFilters, setShowFilters] = useState(false);

  // ============================================
  // DEBOUNCING PARA FILTROS DE TEXTO
  // ============================================
  const [filtrosLocales, setFiltrosLocales] = useState(filtros);
  const [debouncedFiltros, setDebouncedFiltros] = useState(filtros);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFiltros(filtrosLocales);
    }, 500);

    return () => clearTimeout(timer);
  }, [filtrosLocales]);

  useEffect(() => {
    if (
      debouncedFiltros.search !== filtros.search ||
      debouncedFiltros.codigo_ubicacion !== filtros.codigo_ubicacion
    ) {
      setFiltros(debouncedFiltros);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedFiltros]);

  useEffect(() => {
    setFiltrosLocales(filtros);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtros.zona, filtros.linea, filtros.columna, filtros.nivel, filtros.expiry]);

  // eslint-disable-next-line no-unused-vars
  const handleTextFilterChange = (field, value) => {
    setFiltrosLocales(prev => ({ ...prev, [field]: value }));
  };

  // eslint-disable-next-line no-unused-vars
  const handleInstantFilterChange = (field, value) => {
    const newFilters = { ...filtros, [field]: value };
    setFiltros(newFilters);
    setFiltrosLocales(newFilters);
  };

  // ============================================
  // UTILIDADES
  // ============================================

  const isExpired = (fechaVencimiento) => {
    if (!fechaVencimiento) return false;
    const hoy = new Date();
    const vencimiento = new Date(fechaVencimiento);
    return vencimiento < hoy;
  };

  const isNearExpiry = (fechaVencimiento, days = 30) => {
    if (!fechaVencimiento) return false;
    const hoy = new Date();
    const vencimiento = new Date(fechaVencimiento);
    const diffTime = vencimiento - hoy;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 && diffDays <= days;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Sin fecha';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', { year: 'numeric', month: '2-digit', day: '2-digit' });
  };

  // ============================================
  // FUNCIONES DE EXPANSIÓN
  // ============================================

  const toggleZone = (zonaId) => {
    setExpandedZones(prev => ({
      ...prev,
      [zonaId]: !prev[zonaId]
    }));
  };

  const toggleLinea = (lineaId) => {
    setExpandedLineas(prev => ({
      ...prev,
      [lineaId]: !prev[lineaId]
    }));
  };

  const toggleColumna = (columnaId) => {
    setExpandedColumnas(prev => ({
      ...prev,
      [columnaId]: !prev[columnaId]
    }));
  };

  const toggleNivel = (nivelId) => {
    setExpandedNiveles(prev => ({
      ...prev,
      [nivelId]: !prev[nivelId]
    }));
  };

  // eslint-disable-next-line no-unused-vars
  const expandirTodo = () => {
    if (!estructuraCompleta?.zonas) return;
    
    const newExpandedZones = {};
    const newExpandedLineas = {};
    const newExpandedColumnas = {};

    estructuraCompleta.zonas.forEach(zona => {
      newExpandedZones[zona.id] = true;
      zona.lineas?.forEach(linea => {
        newExpandedLineas[linea.id] = true;
        linea.columnas?.forEach(columna => {
          newExpandedColumnas[columna.id] = true;
        });
      });
    });

    setExpandedZones(newExpandedZones);
    setExpandedLineas(newExpandedLineas);
    setExpandedColumnas(newExpandedColumnas);
  };

  // eslint-disable-next-line no-unused-vars
  const contraerTodo = () => {
    setExpandedZones({});
    setExpandedLineas({});
    setExpandedColumnas({});
    setExpandedNiveles({});
  };

  // eslint-disable-next-line no-unused-vars
  const limpiarFiltros = () => {
    const filtrosVacios = {
      search: '',
      zona: '',
      linea: '',
      columna: '',
      nivel: '',
      expiry: 'all',
      codigo_ubicacion: ''
    };
    setFiltros(filtrosVacios);
    setFiltrosLocales(filtrosVacios);
  };

  // ============================================
  // UTILIDAD: EXTRAER POSICIÓN DE CÓDIGO
  // ============================================

  const getPosicionFromCodigo = (codigoUbicacion) => {
    if (!codigoUbicacion) return null;
    const match = codigoUbicacion.match(/(P\d{2})$/);
    return match ? match[1] : null;
  };

  // ============================================
  // RENDER DE PRODUCTO COMPACTO
  // ============================================

  const renderProductCard = (producto) => {
    const expired = isExpired(producto.fecha_vencimiento);
    const nearExpiry = !expired && isNearExpiry(producto.fecha_vencimiento);

    return (
      <div
        key={`${producto.barcode}-${producto.lote}-${producto.ubicacion_id}`}
        style={{
          display: 'flex',
          gap: '10px',
          padding: '10px',
          background: expired ? '#fef2f2' : nearExpiry ? '#fffbeb' : 'white',
          border: `1px solid ${expired ? '#fecaca' : nearExpiry ? '#fde68a' : '#e2e8f0'}`,
          borderRadius: '6px',
          marginBottom: '8px',
          position: 'relative',
          alignItems: 'center'
        }}
      >
        {/* Imagen */}
        {producto.imagen_url && (
          <img
            src={producto.imagen_url}
            alt={producto.descripcion}
            style={{
              width: '50px',
              height: '50px',
              objectFit: 'cover',
              borderRadius: '4px',
              border: '1px solid #e2e8f0',
              flexShrink: 0
            }}
          />
        )}

        {/* Contenido */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Nombre del producto */}
          <div style={{
            fontSize: '13px',
            fontWeight: '600',
            color: '#1e293b',
            marginBottom: '4px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {producto.descripcion}
          </div>

          {/* Info compacta */}
          <div style={{
            fontSize: '11px',
            color: '#64748b',
            display: 'flex',
            gap: '8px',
            flexWrap: 'wrap',
            marginBottom: '4px'
          }}>
            <span style={{ fontFamily: 'monospace' }}>
              {producto.barcode}
            </span>
            <span>•</span>
            <span>
              Lote: {producto.lote}
            </span>
            <span>•</span>
            <span style={{
              color: expired ? '#dc2626' : nearExpiry ? '#ea580c' : '#64748b',
              fontWeight: expired || nearExpiry ? '600' : '400'
            }}>
              {formatDate(producto.fecha_vencimiento)}
            </span>
          </div>

          {/* Cantidades en línea */}
          <div style={{
            display: 'flex',
            gap: '6px',
            flexWrap: 'wrap'
          }}>
            {producto.cantidad_max > 0 && (
              <span style={{
                padding: '2px 8px',
                background: '#dbeafe',
                borderRadius: '4px',
                fontSize: '11px',
                fontWeight: '600',
                color: '#1e40af'
              }}>
                {producto.cantidad_max} {producto.unidad_max}
              </span>
            )}
            {producto.cantidad_med > 0 && (
              <span style={{
                padding: '2px 8px',
                background: '#dbeafe',
                borderRadius: '4px',
                fontSize: '11px',
                fontWeight: '600',
                color: '#1e40af'
              }}>
                {producto.cantidad_med} {producto.unidad_med}
              </span>
            )}
            {producto.cantidad_peq > 0 && (
              <span style={{
                padding: '2px 8px',
                background: '#dbeafe',
                borderRadius: '4px',
                fontSize: '11px',
                fontWeight: '600',
                color: '#1e40af'
              }}>
                {producto.cantidad_peq} {producto.unidad_peq}
              </span>
            )}
            <span style={{
              padding: '2px 8px',
              background: '#dcfce7',
              borderRadius: '4px',
              fontSize: '11px',
              fontWeight: '600',
              color: '#15803d'
            }}>
              {producto.stock_unidades} uds
            </span>
          </div>
        </div>

        {/* Icono de alerta */}
        {(expired || nearExpiry) && (
          <div style={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            background: expired ? '#ef4444' : '#f59e0b',
            color: 'white',
            borderRadius: '50%',
            width: '22px',
            height: '22px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {expired ? <AlertTriangle size={12} /> : <AlertCircle size={12} />}
          </div>
        )}
      </div>
    );
  };

  // ============================================
  // RENDER
  // ============================================

  if (!estructuraCompleta) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '400px',
        color: '#94a3b8'
      }}>
        <div style={{ textAlign: 'center' }}>
          <Warehouse size={48} style={{ marginBottom: '12px', opacity: 0.3 }} />
          <p>Cargando estructura del almacén...</p>
        </div>
      </div>
    );
  }

  const hayFiltrosActivos = filtrosLocales.search || filtrosLocales.zona || filtrosLocales.linea ||
    filtrosLocales.columna || filtrosLocales.nivel || filtrosLocales.expiry !== 'all' || filtrosLocales.codigo_ubicacion;

  // eslint-disable-next-line no-unused-vars
  const isFiltrosDebouncing =
    filtrosLocales.search !== filtros.search ||
    filtrosLocales.codigo_ubicacion !== filtros.codigo_ubicacion;

  return (
    <div style={{
      padding: '24px',
      maxWidth: '1400px',
      margin: '0 auto'
    }}>
      
      {/* Zonas */}
      {estructuraCompleta.zonas?.map(zona => (
        <div
          key={zona.id}
          style={{
            background: 'white',
            borderRadius: '8px',
            border: '1px solid #e2e8f0',
            marginBottom: '16px',
            overflow: 'hidden',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
          }}
        >
          {/* Header de Zona */}
          <div
            onClick={() => toggleZone(zona.id)}
            style={{
              padding: '16px 20px',
              background: '#f8fafc',
              borderBottom: expandedZones[zona.id] ? '1px solid #e2e8f0' : 'none',
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              transition: 'background 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#f1f5f9'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#f8fafc'}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              {expandedZones[zona.id] ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
              <div style={{
                width: '12px',
                height: '12px',
                background: zona.color || '#2563eb',
                borderRadius: '3px'
              }} />
              <div>
                <div style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#1e293b'
                }}>
                  Zona {zona.codigo} - {zona.nombre}
                </div>
                <div style={{
                  fontSize: '12px',
                  color: '#64748b',
                  marginTop: '2px'
                }}>
                  {zona.stats?.total_lineas || 0} líneas • {zona.stats?.total_lotes || 0} productos • {zona.stats?.total_unidades || 0} unidades
                </div>
              </div>
            </div>

            <div style={{
              display: 'flex',
              gap: '8px',
              alignItems: 'center'
            }}>
              <div style={{
                padding: '4px 12px',
                background: zona.stats?.total_lotes > 0 ? '#dcfce7' : '#fee2e2',
                color: zona.stats?.total_lotes > 0 ? '#15803d' : '#dc2626',
                borderRadius: '12px',
                fontSize: '11px',
                fontWeight: '600'
              }}>
                {zona.stats?.total_lotes > 0 ? 'ACTIVA' : 'VACÍA'}
              </div>
            </div>
          </div>

          {/* Contenido de Zona */}
          {expandedZones[zona.id] && (
            <div style={{ padding: '20px' }}>
              {zona.lineas && zona.lineas.length > 0 ? (
                zona.lineas.sort((a, b) => parseInt(a.numero) - parseInt(b.numero)).map(linea => (
                  <div
                    key={linea.id}
                    style={{
                      background: '#fafafa',
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0',
                      marginBottom: '16px',
                      overflow: 'hidden'
                    }}
                  >
                    {/* Header de Línea */}
                    <div
                      onClick={() => toggleLinea(linea.id)}
                      style={{
                        padding: '14px 18px',
                        background: 'white',
                        borderBottom: expandedLineas[linea.id] ? '1px solid #e2e8f0' : 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                      }}>
                        {expandedLineas[linea.id] ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                        <Truck size={18} color="#2563eb" />
                        <div>
                          <div style={{
                            fontSize: '15px',
                            fontWeight: '600',
                            color: '#1e293b'
                          }}>
                            Línea {linea.numero}
                          </div>
                          <div style={{
                            fontSize: '11px',
                            color: '#64748b',
                            marginTop: '2px'
                          }}>
                            {linea.stats?.total_columnas || 0} columnas • {linea.stats?.total_lotes || 0} productos • {linea.stats?.total_unidades || 0} uds
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Contenido de Línea */}
                    {expandedLineas[linea.id] && (
                      <div style={{ padding: '16px' }}>
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                          gap: '12px'
                        }}>
                          {/* ORDENAR COLUMNAS POR NÚMERO */}
                          {linea.columnas?.sort((a, b) => parseInt(a.numero) - parseInt(b.numero)).map(columna => (
                            <div
                              key={columna.id}
                              style={{
                                background: 'white',
                                borderRadius: '8px',
                                border: '1px solid #e2e8f0',
                                overflow: 'hidden',
                                transition: 'box-shadow 0.2s',
                                cursor: 'pointer'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)'}
                              onMouseLeave={(e) => e.currentTarget.style.boxShadow = 'none'}
                            >
                              {/* Header de Columna */}
                              <div
                                onClick={() => toggleColumna(columna.id)}
                                style={{
                                  padding: '12px 14px',
                                  background: '#f8fafc',
                                  borderBottom: expandedColumnas[columna.id] ? '1px solid #e2e8f0' : 'none',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center'
                                }}
                              >
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px'
                                }}>
                                  {expandedColumnas[columna.id] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                  <Box size={16} color="#2563eb" />
                                  <span style={{
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    color: '#1e293b'
                                  }}>
                                    Columna {columna.numero}
                                  </span>
                                </div>
                                <div style={{
                                  fontSize: '11px',
                                  color: columna.stats?.total_lotes > 0 ? '#15803d' : '#94a3b8',
                                  fontWeight: '600'
                                }}>
                                  {columna.stats?.total_unidades || 0} uds
                                </div>
                              </div>

                              {/* Info de Columna */}
                              {!expandedColumnas[columna.id] && (
                                <div style={{ padding: '12px 14px' }}>
                                  <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(2, 1fr)',
                                    gap: '8px',
                                    fontSize: '12px',
                                    color: '#64748b'
                                  }}>
                                    <div>
                                      <div style={{ fontWeight: '600', color: '#475569' }}>Niveles</div>
                                      <div>{columna.stats?.total_niveles || 0}</div>
                                    </div>
                                    <div>
                                      <div style={{ fontWeight: '600', color: '#475569' }}>Ocupados</div>
                                      <div>{columna.stats?.niveles_ocupados || 0}</div>
                                    </div>
                                    <div>
                                      <div style={{ fontWeight: '600', color: '#475569' }}>Productos</div>
                                      <div>{columna.stats?.total_lotes || 0}</div>
                                    </div>
                                    <div>
                                      <div style={{ fontWeight: '600', color: '#475569' }}>Stock</div>
                                      <div>{columna.stats?.total_unidades || 0} uds</div>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* Niveles de la Columna */}
                              {expandedColumnas[columna.id] && (
                                <div style={{ padding: '12px' }}>
                                  {columna.niveles?.sort((a, b) => parseInt(a.numero) - parseInt(b.numero)).map(nivel => (
                                    <div
                                      key={nivel.id}
                                      style={{
                                        marginBottom: '10px',
                                        border: '1px solid #e2e8f0',
                                        borderLeftWidth: '3px',
                                        borderLeftColor: nivel.tipo === 'picking' ? '#10b981' : '#2563eb',
                                        borderRadius: '4px',
                                        overflow: 'hidden',
                                        background: 'white'
                                      }}
                                    >
                                      {/* Header de Nivel */}
                                      <div
                                        onClick={() => toggleNivel(nivel.id)}
                                        style={{
                                          padding: '10px 12px',
                                          background: nivel.tipo === 'picking' ? '#ecfdf5' : '#eff6ff',
                                          borderBottom: expandedNiveles[nivel.id] && nivel.productos?.length > 0 ? '1px solid #e2e8f0' : 'none',
                                          cursor: 'pointer',
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'center'
                                        }}
                                      >
                                        <div style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '8px'
                                        }}>
                                          {expandedNiveles[nivel.id] ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                          <Layers size={14} color={nivel.tipo === 'picking' ? '#10b981' : '#2563eb'} />
                                          <strong style={{ fontSize: '13px' }}>
                                            Nivel {nivel.numero}
                                          </strong>
                                          <span style={{
                                            padding: '2px 8px',
                                            background: nivel.tipo === 'picking' ? '#d1fae5' : '#dbeafe',
                                            color: nivel.tipo === 'picking' ? '#065f46' : '#1e40af',
                                            borderRadius: '3px',
                                            fontSize: '10px',
                                            fontWeight: '600'
                                          }}>
                                            {nivel.tipo.toUpperCase()}
                                          </span>
                                        </div>

                                        <div style={{
                                          fontSize: '11px',
                                          color: nivel.productos?.length > 0 ? '#15803d' : '#94a3b8',
                                          fontWeight: '600'
                                        }}>
                                          {nivel.productos?.length > 0
                                            ? `${nivel.total_productos} prod • ${nivel.total_unidades} uds`
                                            : 'Vacío'}
                                        </div>
                                      </div>

                                      {/* Productos del Nivel - Agrupados por Posición (P01/P02) */}
                                      {expandedNiveles[nivel.id] && nivel.productos && nivel.productos.length > 0 && (
                                        <div style={{ padding: '10px' }}>
                                          {['P01', 'P02'].map(pos => {
                                            const prodsInPos = nivel.productos.filter(p => getPosicionFromCodigo(p.codigo_ubicacion) === pos);
                                            // For P01, also include products without a recognized posicion
                                            const unassigned = pos === 'P01' ? nivel.productos.filter(p => !getPosicionFromCodigo(p.codigo_ubicacion)) : [];
                                            const allProds = [...prodsInPos, ...unassigned];
                                            if (allProds.length === 0) return (
                                              <div key={pos} style={{ marginBottom: '10px' }}>
                                                <div style={{
                                                  fontSize: '11px',
                                                  fontWeight: '700',
                                                  color: '#475569',
                                                  marginBottom: '6px',
                                                  padding: '4px 10px',
                                                  background: '#f0fdf4',
                                                  border: '1px solid #bbf7d0',
                                                  borderRadius: '4px',
                                                  display: 'inline-block'
                                                }}>
                                                  {pos} {pos === 'P01' ? '(Der.)' : '(Izq.)'} — Libre
                                                </div>
                                              </div>
                                            );
                                            return (
                                              <div key={pos} style={{ marginBottom: '10px' }}>
                                                <div style={{
                                                  fontSize: '11px',
                                                  fontWeight: '700',
                                                  color: '#475569',
                                                  marginBottom: '6px',
                                                  padding: '4px 10px',
                                                  background: pos === 'P01' ? '#dbeafe' : '#e0e7ff',
                                                  border: `1px solid ${pos === 'P01' ? '#93c5fd' : '#a5b4fc'}`,
                                                  borderRadius: '4px',
                                                  display: 'inline-block'
                                                }}>
                                                  {pos} {pos === 'P01' ? '(Derecha)' : '(Izquierda)'} — {allProds.length} producto{allProds.length !== 1 ? 's' : ''}
                                                </div>
                                                {allProds.map(producto => renderProductCard(producto))}
                                              </div>
                                            );
                                          })}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div style={{
                  padding: '40px',
                  textAlign: 'center',
                  color: '#94a3b8',
                  fontSize: '14px'
                }}>
                  <Truck size={32} style={{ marginBottom: '12px', opacity: 0.3 }} />
                  <div>No hay líneas en esta zona</div>
                </div>
              )}
            </div>
          )}
        </div>
      ))}

      {estructuraCompleta.zonas?.length === 0 && (
        <div style={{
          background: 'white',
          padding: '60px',
          borderRadius: '8px',
          border: '1px solid #e2e8f0',
          textAlign: 'center',
          color: '#94a3b8'
        }}>
          <Warehouse size={64} style={{ marginBottom: '16px', opacity: 0.3 }} />
          <h3 style={{
            margin: '0 0 8px',
            fontSize: '16px',
            fontWeight: '600',
            color: '#64748b'
          }}>
            Sin resultados
          </h3>
          <p style={{ margin: 0, fontSize: '14px' }}>
            {hayFiltrosActivos
              ? 'No se encontraron productos con los filtros aplicados'
              : 'Este almacén no tiene zonas configuradas'}
          </p>
        </div>
      )}
    </div>
  );
};

export default ListWarehouse;